import produce from 'immer';
import type { ICampaignCommissionConfig, IDeliverable, IDeliverableRound } from '../../redux/buzzbridge/types';
import type { CampaignPremiumRequirement } from '../../types/global/campaignPremiumAccess.types';

export type OffsiteCampaignType = 'standard' | 'direct_hire' | 'sync_upload' | 'commission';

export interface IAppInstall {
    enabled: boolean;
    kind?: 'app_install' | 'web_visit';
    /** Shown on public /r/:code landing (e.g. "Meeteor"). */
    app_display_name?: string;
    /** S3 key / URL for app icon on /r/:code landing. */
    app_icon_url?: string;
    android_package?: string;
    android_store_url?: string;
    ios_store_url?: string;
    web_fallback_url?: string;
    /** Auto-refill float from wallet/card when remaining < 10% of original. */
    auto_reload_enabled?: boolean;
    /** Saved payment method document id for auto-reload. */
    payment_method_id?: string;
}

export interface IOffsiteCampaign {
    brand?: any;
    title: string;
    description: string;
    objectives: string[];
    requirements: string[];
    ideas: string[];
    deliverables: IDeliverable[];
    platforms: string[];
    participants_count: number;
    feature_image?: string;
    feature_image_preview?: string;
    assets?: string;
    total_budget: number;
    funded?: boolean;
    approved?: boolean;
    available_budget?: number;
    status?: string;
    deleted?: boolean;
    start_date?: string;
    end_date?: string;
    location?: {
        type: string;
        coordinates: number[];
        address?: string;
        description?: string;
    };
    physical_product?: boolean;
    is_clipping?: boolean;
    clipping_asset_url?: string;
    world_wide?: boolean;
    reward?: string;
    influencer_tier?: string;
    products?: string[];
    campaign_type?: OffsiteCampaignType;
    sync_publish_date?: string;
    delivery_date?: string;
    recurrent_deliverables?: boolean;
    deliverable_rounds?: IDeliverableRound[];
    base_payment_per_creator_cents?: number;
    premium?: boolean;
    premium_requirements?: CampaignPremiumRequirement[];
    influencer_readme?: string;
    commission?: ICampaignCommissionConfig;
    app_install?: IAppInstall;
}

export const initialState: IOffsiteCampaign = {
    title: '',
    description: '',
    objectives: [],
    requirements: [],
    ideas: [],
    deliverables: [],
    platforms: [],
    participants_count: 1,
    feature_image: '',
    feature_image_preview: '',
    assets: '',
    total_budget: 0,
    funded: false,
    approved: false,
    available_budget: 0,
    status: 'draft',
    deleted: false,
    start_date: '',
    end_date: '',
    location: {
        type: 'Point',
        coordinates: [],
        address: '',
        description: '',
    },
    physical_product: false,
    campaign_type: 'standard',
    sync_publish_date: '',
    delivery_date: '',
    recurrent_deliverables: false,
    deliverable_rounds: [],
    premium: false,
    premium_requirements: [],
    is_clipping: false,
    clipping_asset_url: '',
    world_wide: false,
    influencer_readme: '',
};

type Action =
    | { type: 'FIELD'; field: string; value: any }
    | { type: 'RESET' }
    | { type: 'SET_STATE'; payload: IOffsiteCampaign };

export const reducer = (state: IOffsiteCampaign = initialState, action: Action): IOffsiteCampaign => {
    switch (action.type) {
        case 'FIELD':
            return produce(state, draft => {
                (draft as any)[action.field] = action.value;
            });
        case 'RESET':
            return initialState;
        case 'SET_STATE':
            return action.payload;
        default:
            return state;
    }
};
